import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, CircleDollarSign, LogOut, Search, ShieldCheck, TrendingDown, UserCheck, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KpiCard, NativeSelect } from "@/components/common/Page";
import { ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const stats = useFetch("/admin/stats");
  const teachers = useFetch(`/admin/teachers?q=${encodeURIComponent(q)}`);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      stats.reload();
      teachers.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const s = stats.data;
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/80 px-4 backdrop-blur-md sm:px-8">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><span className="font-heading text-lg font-bold">ProfessorHub · Admin</span></div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout-btn"><LogOut className="mr-2 h-4 w-4" />Sair</Button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <h1 className="mb-6 font-heading text-3xl font-extrabold tracking-tight" data-testid="admin-title">Painel administrativo</h1>
        {stats.error ? <ErrorState error={stats.error} /> : !s ? <LoadingState rows={2} /> : (
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Professores cadastrados" value={s.professores} icon={Users} testId="admin-kpi-teachers" />
            <KpiCard label="Ativos (30 dias)" value={s.ativos} icon={UserCheck} tone="text-accent" testId="admin-kpi-active" />
            <KpiCard label="Novos cadastros (30 dias)" value={s.novos_30d} icon={UserPlus} testId="admin-kpi-new" />
            <KpiCard label="Assinantes pagos" value={s.assinantes_pagos} icon={CircleDollarSign} tone="text-accent" testId="admin-kpi-paid" />
            <KpiCard label="Usuários gratuitos" value={s.gratuitos} icon={Users} testId="admin-kpi-free" />
            <KpiCard label="Receita recorrente mensal" value={brl(s.mrr)} icon={CircleDollarSign} testId="admin-kpi-mrr" />
            <KpiCard label="Cancelamentos (30 dias)" value={s.cancelamentos_30d} icon={TrendingDown} tone="text-destructive" testId="admin-kpi-cancel" />
            <KpiCard label="Contas bloqueadas" value={s.bloqueados} icon={Ban} tone="text-destructive" testId="admin-kpi-blocked" />
          </div>
        )}
        <div className="mb-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar professor por nome ou e-mail" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
        </div>
        {teachers.error ? <ErrorState error={teachers.error} /> : !teachers.data ? <LoadingState /> : (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-teachers-table">
              <thead><tr className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3">Professor</th><th className="p-3">Cidade</th><th className="p-3">Cadastro</th><th className="p-3">Alunos</th><th className="p-3">Plano</th><th className="p-3">Assinatura</th><th className="p-3">Conta</th><th className="p-3" />
              </tr></thead>
              <tbody>
                {teachers.data.map((t) => (
                  <tr key={t.id} className="border-b border-border/50 last:border-0" data-testid={`admin-teacher-row-${t.id}`}>
                    <td className="p-3"><p className="font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{t.email}</p></td>
                    <td className="p-3 text-muted-foreground">{t.city ? `${t.city}/${t.state}` : "—"}</td>
                    <td className="p-3 font-mono text-xs">{fmtDate(t.created_at?.slice(0, 10))}</td>
                    <td className="p-3 font-mono">{t.students_count}</td>
                    <td className="p-3">
                      <NativeSelect className="h-9 w-36" value={t.plan_id} onChange={(e) => act(() => api.put(`/admin/teachers/${t.id}/plan`, { plan_id: e.target.value }), "Plano alterado")} data-testid={`admin-plan-select-${t.id}`}>
                        <option value="free">Gratuito</option><option value="basic">Básico</option><option value="pro">Profissional</option>
                      </NativeSelect>
                    </td>
                    <td className="p-3 text-xs"><StatusBadge status={t.subscription_status} /><span className="ml-1 text-muted-foreground">{t.subscription_provider || ""}</span></td>
                    <td className="p-3"><StatusBadge status={t.status} testId={`admin-status-${t.id}`} /></td>
                    <td className="p-3 text-right">
                      {t.status === "blocked"
                        ? <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/teachers/${t.id}/unblock`), "Conta desbloqueada")} data-testid={`admin-unblock-${t.id}`}>Desbloquear</Button>
                        : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(() => api.post(`/admin/teachers/${t.id}/block`), "Conta bloqueada")} data-testid={`admin-block-${t.id}`}>Bloquear</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-muted-foreground">Por privacidade (LGPD), o painel exibe apenas a quantidade de alunos de cada professor — nunca os dados pessoais dos alunos.</p>
      </main>
    </div>
  );
}
